import type { StockShortKeyData } from "@/models/stock";

const finite = (value: unknown): value is number => typeof value === "number" && Number.isFinite(value);
const fmt = (value: number, digits = 2) => value.toLocaleString("en-US", { maximumFractionDigits: digits });

/**
 * One sentence stating a stock's short-interest headline numbers, for meta
 * descriptions: the string search snippets and AI answer engines quote. Empty
 * when there are no numbers, so callers can prepend it unconditionally.
 * e.g. "GME short interest: 10.3% of free float, 5.6 days to cover, cost to borrow 0.42% (ORTEX, 2026-10-19)."
 */
export const shortHeadline = (ticker: string, data: StockShortKeyData | null | undefined): string => {
    if (!data) return "";
    const parts: string[] = [];
    if (finite(data.siPercentFreeFloat)) parts.push(`${fmt(data.siPercentFreeFloat, 1)}% of free float`);
    if (finite(data.daysToCover)) parts.push(`${fmt(data.daysToCover, 1)} days to cover`);
    if (finite(data.costToBorrow)) parts.push(`cost to borrow ${fmt(data.costToBorrow)}%`);
    if (!parts.length) return "";
    return `${ticker} short interest: ${parts.join(", ")} (ORTEX${data.asOf ? `, ${data.asOf}` : ""}).`;
};

/** Query params carrying the numbers to the /api/og share card. */
export const shortOgParams = (data: StockShortKeyData | null | undefined): Record<string, string> => {
    if (!data) return {};
    const params: Record<string, string> = {};
    if (finite(data.siPercentFreeFloat)) params.si = fmt(data.siPercentFreeFloat, 2);
    if (finite(data.daysToCover)) params.dtc = fmt(data.daysToCover, 1);
    if (finite(data.costToBorrow)) params.ctb = fmt(data.costToBorrow, 2);
    if (data.asOf && /^\d{4}-\d{2}-\d{2}$/.test(data.asOf)) params.asof = data.asOf;
    return params;
};
